<template>
  <section class="border-b border-gray-200 md:px-12 md:pr-0 px-8 lg:pb-5 py-0" v-if="!hideCartButton">
    <div class="flex flex-row gap-[0.7142857rem]">
      <div class="flex flex-row w-[6.0714286rem]" v-if="!props?.product?.loyaltyOnly">
        <div class="text-4">
          <label for="sticky-add" class="sr-only">
            Quantity
          </label>
          <input
          id="sticky-add"
          class="px-4 w-[4.214285rem] text-center pt-[0.357143rem] pb-[0.428571rem] h-[3.428571rem] hover:border-gray-600 border border-ash-300 rounded-[0.428571rem] rounded-tr-none rounded-br-none"
          type="number"
          inputmode="numeric"
          :value="quantity"
          @input="onInput"
          @keydown="blockAlpha"
          @blur="handleBlur"
          aria-label="Quantity box editable"
        />
        </div>
        <div class="flex flex-col">
          <span
            class="flex justify-center items-center border cursor-pointer border-gray-500 h-[1.71429rem] w-[1.78571rem] text-center rounded-tr-[0.428571rem] hover:text-gold-300"
            @click.prevent="increase"
            aria-label="Increase quantity"
            role="button"
            id="increase-qty"
            tabindex="0"
            @keyup.enter.prevent="increase" 
            @keyup.space.prevent="increase"
          >
            <Icon icon="angle-up" />
          </span>
          <span
            class="flex justify-center items-center border cursor-pointer border-gray-500 h-[1.71429rem] w-[1.78571rem] text-center border-t-0 rounded-br-[0.428571rem]  hover:text-gold-300"
            @click.prevent="decrease"
            aria-label="Decrease quantity"
            role="button"
            id="decrease-qty"
            tabindex="0"
            @keyup.enter.prevent="decrease" 
            @keyup.space.prevent="decrease"
            :aria-disabled="decreaseButtonDisabled"
          >
            <Icon icon="angle-down" />
          </span>
        </div>
        <div aria-live="polite" class="sr-only"> 
          Quantity: {{ quantity }} 
        </div>
      </div>
      <Button
        :id="addToCartId"
        :aria-label="buttonText  == 'Add to Cart' ? 'Add to cart opens simulated dialog' : buttonText"
        :title="buttonText  == 'Add to Cart' ? 'Add to cart opens simulated dialog' : buttonText"
        type="button"
        @click.prevent="handleClick(addToCartId)"
        size="sm"
        :border="false"
        theme="LIGHT"
        class="relative font-medium rounded-lg gap-1 cursor-pointer w-full px-[1rem] bg-ash-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
        :class="[ { 'py-[1.071428rem]': props?.product?.loyaltyOnly }, { 'bg-red-400': product?.outOfStock }]"
        :disabled="overMaxCartQty || product?.extendedBackorder || product?.outOfStock"
      >
        {{ buttonText }}
      </Button>
    </div>
    <div v-if="props.product?.maxCartQty" class="text-ash-100 mt-4 mb-4"> {{`(Limited to ${props.product.maxCartQty} per order)`}}</div>
    <p
      v-if="showLimitMessage || overMaxCartQty"
      class="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-red-400 text-white px-4 py-2 rounded-md shadow-lg"
    >
      {{ maxQtyMessage }}
    </p>

    <p
      v-if="showProductErrorMessage"
      role="alert"
      aria-atomic="true"
      class="fixed bottom-8 right-[2.86rem] bg-red-400 text-white px-5 py-5 rounded-md shadow-lg"
    >
      {{ addToCartErrorMessage }}
    </p>
  </section>

  <section class="border-b border-gray-200 md:px-12 md:pr-0 px-8 pr-8 pb-5 pt-0" v-if="showInsufficientButton">
    <div>
      <Button
        id="insufficient-button"
        title="Insufficient points"
        aria-label="Insufficient points"
        type="button"
        size="sm"
        :border="false"
        theme="LIGHT"
        class="relative rounded-lg gap-1 cursor-pointer w-full !cursor-not-allowed py-[1.071428rem] !bg-ash-500"
        :disabled="true"
      >
        Insufficient points
      </Button>
    </div>
  </section>
  
   <section class="border-b border-gray-200 md:px-12 md:pr-0 px-8 fixed bottom-0 w-full z-[200] p-4 bg-white border-t border-black/10 lg:hidden" v-if="product?.outOfStock || !hideCartButton">
    <div class="flex flex-row gap-[0.7142857rem]">
      <div class="flex flex-row" v-if="!props?.product?.loyaltyOnly">
        <div class="text-4">
          <label for="sticky-input" class="sr-only">
            Quantity
          </label>
          <input
            id="sticky-input"
            class="px-4 min-w-[4.0714286rem] text-center pt-[0.357143rem] pb-[0.428571rem] h-[3.4286rem] border border-ash-300 rounded-[0.428571rem] rounded-tr-none rounded-br-none"
            type="number"
            min="1"
            :max="maxQuantity"
            step="1"
            v-model="quantity"
            @blur="handleBlur"
            aria-label="Quantity box editable"
          />
        </div>
        <div class="flex flex-col">
          <span
            class="flex justify-center items-center border cursor-pointer border-gray-500 h-[1.71429rem] w-[1.78571rem] text-center rounded-tr-[0.428571rem] hover:text-gold-300"
            @click.prevent="increase"
            aria-label="Increase quantity"
          >
            <Icon icon="angle-up" />
          </span>
          <span
            class="flex justify-center items-center border cursor-pointer border-gray-500 h-[1.71429rem] w-[1.78571rem] text-center border-t-0 rounded-br-[0.428571rem]  hover:text-gold-300"
            @click.prevent="decrease"
            aria-label="Decrease quantity"
          >
            <Icon icon="angle-down" />
          </span>
        </div>
      </div>
      <Button
        id="add-prod-to-cart"
        :title="buttonText"
        :aria-label="buttonText"
        type="button"
        @click.prevent="handleClick('add-prod-to-cart')"
        size="sm"
        :border="false"
        theme="LIGHT"
        class="relative rounded-lg gap-1 cursor-pointer w-full !bg-ash-500 py-[1.15rem] px-[1.143rem]"
        :disabled="overMaxCartQty || product?.outOfStock || product?.extendedBackorder"
      >
        {{ buttonText }}
      </Button>
      <div aria-live="polite" class="sr-only"> 
        Quantity: {{ quantity }} 
      </div>
    </div>
    <div v-if="props.product?.maxCartQty" class="text-ash-100 mt-4 mb-4"> {{`(Limited to ${props.product.maxCartQty} per order)`}}</div>
    <p
      v-if="showLimitMessage || overMaxCartQty"
      class="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-red-400 text-white px-4 py-2 rounded-md shadow-lg"
    >
      {{ maxQtyMessage }}
    </p>
  </section>
</template>

<script setup lang="ts">
import { ref, computed ,watch, onMounted, onUnmounted } from 'vue'
import Icon from '../../../atom/icon.vue'
import Button from '../../../atom/button.vue'
import { useCartModalStore } from '~/stores/cartModalStore'
import siteService from '~/composables/siteService'
import { useUserStore } from '~/stores/userStore'
import { useLocaleStore } from '~/stores/localeStore'
import { resolveAddToCartFrequency } from '~~/shared/utils/orderGrooveFrequency'

const userStore = useUserStore()
const localeStore = useLocaleStore()
const decreaseButtonDisabled = ref(false)

const props = defineProps<{
  productId: number;
  frequency:number;
  product?: {
    id: number;
    name: string;
    price?: string;
    loyaltyPointCost?: number;
    loyaltyOnly?: boolean;
    purchaseableWithLoyaltyPoints?: boolean;
    maxCartQty?: number;
    outOfStock?: boolean;
    extendedBackorder?: boolean;
    active?: boolean;
  };
  addToCartId: string
  ogOfferElement?: Element | null
  ogOfferLocation?: string
}>();

const emit = defineEmits<{ (e: "added"): void }>();
const cartModalStore = useCartModalStore();
const quantity = ref<number>(1);
const showLimitMessage = ref(false);
const showProductErrorMessage = ref(false);
const errorProductName = ref('');

const triggerLimitMessage = () => {
  showLimitMessage.value = true;
  setTimeout(() => {
    showLimitMessage.value = false;
  }, 5000);
}

const triggerErrorAddToCart = (productName:string) =>{
   showProductErrorMessage.value = true;
    errorProductName.value = productName as string;
    setTimeout(() => {
    showProductErrorMessage.value = false;
  }, 5000);
}

const maxQuantity = computed(() =>
  props.product?.maxCartQty ? props.product.maxCartQty : 999
);

const hideCartButton = computed(() => {
  if (props?.product?.loyaltyOnly && (!userStore.loggedIn || props.product.purchaseableWithLoyaltyPoints === false)) {
    return true
  }

  if (props?.product?.loyaltyOnly && userStore.loggedIn && showInsufficientButton.value) {
    return true
  }
  return false
})

const showInsufficientButton = computed(() => {
  return (userStore.loggedIn && props?.product?.loyaltyOnly && props.product.purchaseableWithLoyaltyPoints !== false) && hasInsufficientPoints.value
})

const customerLoyaltyPoint = computed(() => {
  return localeStore.loyaltyPointsValue;
})

const hasInsufficientPoints = computed(() => {
  return (props.product?.loyaltyPointCost ?? 0) > customerLoyaltyPoint.value
})

const overMaxCartQty = computed(() =>
  props.product?.maxCartQty ? quantity.value > props.product.maxCartQty : false
);

const maxQtyMessage = computed(() =>
  `Limited to ${maxQuantity.value} per order`
);

const addToCartErrorMessage = computed(() =>
  `An error occurred adding ${errorProductName.value}`
);

const insufficientPointsForPurchase = computed(() => {
  if (!props.product?.purchaseableWithLoyaltyPoints) return false;
  if (!props.product?.loyaltyPointCost) return false;
  return customerLoyaltyPoint.value < props.product.loyaltyPointCost;
});

const loyaltyButtonText = computed(() => {
  if (!props.product?.purchaseableWithLoyaltyPoints) return "Add to Cart";
  if (props.product?.loyaltyOnly && !insufficientPointsForPurchase.value) {
    return "Add to Cart";
  } else if (props.product?.loyaltyOnly) {
    return "Insufficient points";
  } else {
    return `Add for ${props.product?.loyaltyPointCost} points`;
  }
});

const buttonText = computed(() => {
  if (props.product?.outOfStock) {
    return "Out of Stock - Pre-Order";
  }
  if (props.product?.purchaseableWithLoyaltyPoints) {
    return loyaltyButtonText.value;
  }
   if (selectedOGOption.value === 'optin') {
    return 'Subscribe'
  }
  return "Add to Cart";
});

const isOgOptOutSelected = (offerEl: Element | null) => {
  if (!offerEl) return false

  const optOutSelected = offerEl.querySelector(
    'og-optout-button[active], og-optout-button[selected], og-optout-button[aria-pressed="true"]'
  )
  if (optOutSelected) return true

  const optInSelected = offerEl.querySelector(
    'og-optin-button[active], og-optin-button[selected], og-optin-button[aria-pressed="true"]'
  )
  if (optInSelected) return false

  return false
}

const getOgRawFrequencyValue = (offerEl: Element | null) => {
  if (!offerEl) return ''

  const activeOptIn = offerEl.querySelector(
    'og-optin-button[active], og-optin-button[selected], og-optin-button[aria-pressed="true"], og-optin-button[frequency-match]'
  )
  const optInFrequency = activeOptIn?.getAttribute('frequency')?.trim() ?? ''
  if (optInFrequency) {
    return optInFrequency
  }

  const offerFrequency = offerEl.getAttribute('frequency')?.trim() ?? ''
  if (offerFrequency) {
    return offerFrequency
  }

  const selectEl = offerEl.querySelector(
    'og-select-frequency select, select[aria-label="Delivery frequency"], select[aria-label="Deliver every"], select'
  )
  if (selectEl instanceof HTMLSelectElement) {
    return selectEl.value?.trim() ?? ''
  }
  return ''
}


const frequencyForAddToCart = () => {
  const offerEl = resolveOgOfferElement()
  const rawOgFrequency = getOgRawFrequencyValue(offerEl)

  if (selectedOGOption.value === 'optout' || rawOgFrequency === '1_1' || isOgOptOutSelected(offerEl)) {
    return 0
  }

  return resolveAddToCartFrequency({
    ogOffer: offerEl ?? props.ogOfferElement ?? null,
    ogOfferLocation: props.ogOfferLocation ?? 'PDP',
    subscriptionFrequency: props.frequency,
  })
}

const addToCart = async (productId: number | undefined, qty: number, frequency:number, productName?:string, id?:string) => {
  if (!productId) {
    return;
  }
  try {
    const response:any = await siteService().addToCart(
      productId,
      qty,
      frequency,
      props?.product?.loyaltyOnly
    )
    const modalData = response.lines.find(line => line.productId === productId);
    const totalQuantity = response.lines.reduce((sum, line) => sum + line.quantity, 0);

    cartModalStore.open(
      {
        name: modalData.name,
        subtotal: modalData.subtotal,
        imageUrl: modalData.imageUrl,
        quantity: modalData.quantity,
        frequency,
        id: id
      },
     totalQuantity
    );
    
  } catch (err) {
    triggerErrorAddToCart(productName as string)
  }
};

const handleClick = (id: string) => {
  addToCart(
    props.productId,
    quantity.value,
    frequencyForAddToCart(),
    props?.product?.name,
    id
  );
  emit("added");  
};

const increase = () => {
  if (quantity.value < maxQuantity.value) {
    quantity.value++;
    if (quantity.value > 1) {
      decreaseButtonDisabled.value = false;
    }
  } else {
    triggerLimitMessage();
  }
}

const decrease = () => {
  decreaseButtonDisabled.value = quantity.value === 1;
  if (quantity.value > 1) {
    quantity.value--;
  }
}

const handleBlur = (e: FocusEvent) => {
  const el = e.target as HTMLInputElement
  if (el.value === '' || Number(el.value) === 0) {
    quantity.value = 1
    el.value = '1'
    return
  }
  if (quantity.value > maxQuantity.value) {
    quantity.value = maxQuantity.value
    el.value = String(maxQuantity.value)
  }
}

const onInput = (e: Event) => {
  const el = e.target as HTMLInputElement
  const cleaned = el.value.replace(/\D/g, '')
  if (cleaned === '') {
    el.value = ''
    return
  }
  quantity.value = Number(cleaned)
}

const blockAlpha = (e: KeyboardEvent) => {
  if (
    !/[0-9]/.test(e.key) &&
    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
  ) {
    e.preventDefault()
  }
}

const selectedOGOption = ref<'optin' | 'optout' | ''>('')
const ogOfferObserver = ref<MutationObserver | null>(null)
const observedOgOfferElement = ref<Element | null>(null)

const disconnectOgOfferObserver = () => {
  ogOfferObserver.value?.disconnect()
  ogOfferObserver.value = null
  observedOgOfferElement.value = null
}

const resolveOgOfferElement = (target?: Element | null) => {
  const byTarget = target?.closest('og-offer')
  if (byTarget) return byTarget

  if (props.ogOfferElement) return props.ogOfferElement

  const byLocation = document.querySelector(
    `og-offer[location="${props.ogOfferLocation ?? 'PDP'}"]`
  )
  if (byLocation) return byLocation

  return document.querySelector('og-offer')
}

const syncSelectedOGOptionFromOffer = (offerEl: Element | null) => {
  if (!offerEl) {
    selectedOGOption.value = ''
    return
  }

  if (isOgOptOutSelected(offerEl)) {
    selectedOGOption.value = 'optout'
    return
  }

  const optInSelected = offerEl.querySelector(
    'og-optin-button[active], og-optin-button[selected], og-optin-button[aria-pressed="true"]'
  )
  if (optInSelected) {
    selectedOGOption.value = 'optin'
    return
  }

  if (offerEl.getAttribute('subscribed') !== null) {
    selectedOGOption.value = 'optin'
    return
  }

  selectedOGOption.value = ''
}

const observeOgOfferState = (offerEl: Element | null) => {
  if (!offerEl) return
  if (observedOgOfferElement.value === offerEl && ogOfferObserver.value) return

  disconnectOgOfferObserver()

  const observer = new MutationObserver(() => {
    syncSelectedOGOptionFromOffer(offerEl)
  })

  observer.observe(offerEl, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['subscribed', 'active', 'selected', 'aria-pressed']
  })

  ogOfferObserver.value = observer
  observedOgOfferElement.value = offerEl
}

const selectOgAdminDefaultFrequencyOption = (offerEl: Element | null) => {
  if (!offerEl) return

  const frequencySelect = offerEl.querySelector(
    'og-select-frequency select, select[aria-label="Delivery frequency"], select[aria-label="Deliver every"], select'
  )
  if (!(frequencySelect instanceof HTMLSelectElement) || frequencySelect.options.length === 0) {
    return
  }

  const activeOptIn = offerEl.querySelector(
    'og-optin-button[active], og-optin-button[selected], og-optin-button[aria-pressed="true"], og-optin-button[frequency-match]'
  )
  const optInFrequency = activeOptIn?.getAttribute('frequency')?.trim() ?? ''
  const offerFrequency = offerEl.getAttribute('frequency')?.trim() ?? ''

  const selectedFromAdmin =
    (frequencySelect.querySelector('option[selected]') as HTMLOptionElement | null) ??
    Array.from(frequencySelect.options).find((opt) => opt.defaultSelected) ??
    null

  const fallbackFirstOption = frequencySelect.options.item(0)
  const fromOptIn = optInFrequency
    ? (
        Array.from(frequencySelect.options).find(
          (opt) => (opt.getAttribute('value') ?? '') === optInFrequency
        ) ?? null
      )
    : null
  const fromOffer = offerFrequency
    ? (
        Array.from(frequencySelect.options).find(
          (opt) => (opt.getAttribute('value') ?? '') === offerFrequency
        ) ?? null
      )
    : null
  const targetOption = fromOptIn ?? fromOffer ?? selectedFromAdmin ?? fallbackFirstOption
  if (!targetOption) return

  const targetValue = targetOption.getAttribute('value') ?? ''
  if (!targetValue) return

  const targetIndex = Array.from(frequencySelect.options).findIndex(
    (opt) => (opt.getAttribute('value') ?? '') === targetValue
  )

  if (frequencySelect.value !== targetValue) {
    frequencySelect.value = targetValue
  }
  if (targetIndex > -1 && frequencySelect.selectedIndex !== targetIndex) {
    frequencySelect.selectedIndex = targetIndex
  }

  offerEl.setAttribute('frequency', frequencySelect.value)
  frequencySelect.dispatchEvent(new Event('input', { bubbles: true }))
  frequencySelect.dispatchEvent(new Event('change', { bubbles: true }))
}

const ensureOgSubscribeFrequency = (offerEl: Element | null) => {
  const attempts = [0, 120, 300]
  attempts.forEach((delay) => {
    setTimeout(() => {
      selectOgAdminDefaultFrequencyOption(offerEl)
    }, delay)
  })
}

const handleOGClick = (e: Event) => {
  const clickTarget = e.target as Element | null
  const target = clickTarget?.closest('og-optout-button, og-optin-button')
  if (!target) return

  const isOptIn = target.tagName.toLowerCase() === 'og-optin-button'
  selectedOGOption.value = isOptIn ? 'optin' : 'optout'

  if (!isOptIn) return

  const offerEl = resolveOgOfferElement(target)
  ensureOgSubscribeFrequency(offerEl)
}

const syncOgButtonTextState = () => {
  const attempts = [0, 120, 300, 700, 1200, 2000]
  attempts.forEach((delay) => {
    setTimeout(() => {
      const offerEl = resolveOgOfferElement()
      syncSelectedOGOptionFromOffer(offerEl)
      observeOgOfferState(offerEl)
    }, delay)
  })
}

onMounted(() => {
  document.addEventListener('click', handleOGClick)
  syncOgButtonTextState()
})

watch(
  () => [props.ogOfferElement, props.ogOfferLocation],
  () => {
    disconnectOgOfferObserver()
    syncOgButtonTextState()
  },
  { flush: 'post' }
)

watch(
  () => [props.productId, props.product?.id],
  () => {
    disconnectOgOfferObserver()
    selectedOGOption.value = ''
    syncOgButtonTextState()
  },
  { flush: 'post' }
)

watch(
  () => [props.productId, props.product?.id],
  () => {
    selectedOGOption.value = ''
    syncOgButtonTextState()
  },
  { flush: 'post' }
)

onUnmounted(() => {
  disconnectOgOfferObserver()
  document.removeEventListener('click', handleOGClick)
})

</script>
